import { useState, Fragment } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { Voucher, VoucherType, VoucherStatus } from "@/lib/accounting/types";
import { Button } from "@/components/ui/button";
import { VoucherEntryForm } from "./VoucherEntryForm";
import { CheckCircle2, XCircle, Clock, Eye, Search, Trash2, ChevronDown, ChevronRight, Boxes, PackageCheck, PackageMinus, PackageX, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface VoucherListProps {
  type: VoucherType;
  title: string;
  description: string;
  hideHeader?: boolean;
}

const statusIcon: Record<VoucherStatus, { icon: typeof CheckCircle2; className: string }> = {
  Approved: { icon: CheckCircle2, className: "text-success bg-success/10" },
  Pending: { icon: Clock, className: "text-warning bg-warning/10" },
  Rejected: { icon: XCircle, className: "text-destructive bg-destructive/10" },
  Draft: { icon: Clock, className: "text-muted-foreground bg-muted" },
  Cancelled: { icon: Trash2, className: "text-muted-foreground bg-muted/50" },
};

export function VoucherList({ type, title, description, hideHeader }: VoucherListProps) {
  const { getVouchersByType, approveVoucher, rejectVoucher, deleteVoucher, stockItems } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();
  const perms = getAccountingPermissions(role, type);
  const vouchers = getVouchersByType(type);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = vouchers.filter((v) =>
    v.voucherNumber.toLowerCase().includes(search.toLowerCase()) ||
    v.partyName?.toLowerCase().includes(search.toLowerCase()) ||
    v.narration.toLowerCase().includes(search.toLowerCase())
  );

  const handleApprove = async (v: Voucher) => {
    // Authoritative stock validation for Sales vouchers before approval
    if ((v.type === "Sales" || v.type === "Delivery Note") && v.items && v.items.length > 0) {
      const rawItems: any[] = Array.isArray(v.items)
        ? v.items
        : typeof v.items === "string"
        ? (() => { try { return JSON.parse(v.items); } catch { return []; } })()
        : [];

      for (const itm of rawItems) {
        if (itm.itemType === "service" || itm.isService) continue;
        const targetItemId = itm.itemId || itm.stockItemId || itm.id;
        const matchedItem = targetItemId
          ? stockItems?.find((s) => s.id === targetItemId)
          : stockItems?.find((s) => s.name.trim().toLowerCase() === String(itm.name || itm.description || "").trim().toLowerCase());

        const reqQty = Number(itm.qty || itm.quantity || 0);
        const availQty = Number(matchedItem?.currentQty || 0);
        if (!matchedItem || availQty < reqQty) {
          toast({
            title: "Insufficient Stock Warning",
            description: `Cannot approve ${v.voucherNumber}: Available stock for "${matchedItem?.name || itm.name || 'item'}" is ${availQty}, but ${reqQty} was requested.`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    const res = await approveVoucher(v.id);
    if (res && typeof res === "object" && (res as any).success === false) {
      toast({ title: "Approval Error", description: (res as any).error || "Failed to approve voucher", variant: "destructive" });
      return;
    }
    toast({ title: "Approved", description: `${v.voucherNumber} approved` });
  };

  const handleReject = (v: Voucher) => {
    rejectVoucher(v.id);
    toast({ title: "Rejected", description: `${v.voucherNumber} rejected`, variant: "destructive" });
  };

  const handleDelete = async (v: Voucher) => {
    const res = await deleteVoucher(v.id);
    if (res && typeof res === "object" && (res as any).success === false) {
      toast({ title: "Cannot Cancel", description: (res as any).error || "Could not cancel voucher", variant: "destructive" });
      return;
    }
    toast({ title: "Cancelled", description: `${v.voucherNumber} cancelled` });
  };

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-display text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          <VoucherEntryForm type={type} />
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`}
            className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <div className="text-xs text-muted-foreground">{filtered.length} entries</div>
      </div>

      <div className="rounded-lg bg-card border border-border shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider w-8"></th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Voucher No</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Party</th>
                <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Amount</th>
                <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const sc = statusIcon[v.status];
                const isExpanded = expandedId === v.id;
                return (
                  <Fragment key={v.id}>
                    <tr
                      key={v.id}
                      className="border-t border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : v.id)}
                    >
                      <td className="py-3 px-4">
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      </td>
                      <td className="py-3 px-4 font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                        {v.voucherNumber}
                      </td>
                      <td className="py-3 px-4 text-foreground">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                      <td className="py-3 px-4 text-foreground">{v.partyName || v.narration.slice(0, 40)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${sc.className}`}>
                          <sc.icon className="h-3 w-3" />
                          {v.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {v.status === "Pending" && perms.canApprove && (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 text-success hover:text-success hover:bg-success/10" onClick={() => handleApprove(v)}>
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleReject(v)}>
                                <XCircle className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {v.status !== "Cancelled" && perms.canDelete && (
                            <Button variant="ghost" size="sm" className="h-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(v)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${v.id}-detail`} className="bg-muted/20">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="space-y-4">
                            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1.5 p-2.5 rounded-md bg-muted/40 border border-border/40">
                              <div><strong>Voucher No:</strong> <span className="font-semibold text-[#2563EB] dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">{v.voucherNumber}</span></div>
                              {v.invoiceNumber && (
                                <div><strong>Invoice Ref:</strong> <span className="font-semibold text-[#2563EB] dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">#{v.invoiceNumber}</span></div>
                              )}
                              <div><strong>Date:</strong> <span className="text-foreground font-medium">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>
                              <div><strong>Party:</strong> <span className="text-foreground font-medium">{v.partyName || "—"}</span></div>
                              <div><strong>Narration:</strong> <span className="text-foreground">{v.narration}</span></div>
                              <div><strong>Created by:</strong> <span className="text-foreground">{v.createdBy || "system"}</span></div>
                              {v.approvedBy && <div><strong>Approved by:</strong> <span className="text-foreground">{v.approvedBy}</span></div>}
                            </div>

                            {/* Line Items & Stock Tracking Breakdown (When invoice has products/services) */}
                            {(() => {
                              const rawItems: any[] = Array.isArray(v.items)
                                ? v.items
                                : typeof v.items === "string"
                                ? (() => { try { return JSON.parse(v.items); } catch { return []; } })()
                                : [];

                              if (!rawItems || rawItems.length === 0) return null;

                              return (
                                <div className="space-y-2 pt-1 border-t border-border/50">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                      <Boxes className="h-3.5 w-3.5 text-blue-500" />
                                      <span>Invoice Items & Inventory Tracking</span>
                                    </div>
                                    <span className="text-[11px] text-muted-foreground font-mono">
                                      {rawItems.length} item{rawItems.length > 1 ? "s" : ""}
                                    </span>
                                  </div>
                                  <div className="rounded-lg border border-border/70 overflow-hidden bg-card/60 shadow-2xs">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="bg-muted/50 border-b border-border/60 text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium">
                                          <th className="text-left py-2 px-3">Item / Description</th>
                                          <th className="text-left py-2 px-3">Linked Catalogue Item</th>
                                          <th className="text-left py-2 px-3">Type</th>
                                          <th className="text-right py-2 px-3">Qty</th>
                                          <th className="text-right py-2 px-3">Unit Cost</th>
                                          <th className="text-right py-2 px-3">GST %</th>
                                          <th className="text-right py-2 px-3">Total (₹)</th>
                                          <th className="text-left py-2 px-3">Stock Update Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rawItems.map((itm, iIdx) => {
                                          const isService = itm.itemType === "service" || itm.isService;
                                          const targetItemId = itm.itemId || itm.stockItemId || itm.id;
                                          const matchedItem = targetItemId
                                            ? stockItems?.find((si) => si.id === targetItemId)
                                            : stockItems?.find(
                                                (si) => si.name.trim().toLowerCase() === String(itm.name || itm.description || "").trim().toLowerCase()
                                              );
                                          const itemUnit = itm.unit || matchedItem?.unit || (isService ? "Serv" : "Nos");
                                          const qty = Number(itm.qty || itm.quantity || 0);
                                          const rate = Number(itm.rate || itm.unitPrice || 0);
                                          const amount = itm.amount !== undefined ? Number(itm.amount) : Math.round(qty * rate * 100) / 100;

                                          return (
                                            <tr key={iIdx} className="border-b border-border/30 last:border-b-0 hover:bg-muted/20">
                                              <td className="py-2 px-3 font-medium text-foreground">
                                                <div>{itm.name || itm.description || "—"}</div>
                                                {itm.description && itm.name && itm.description !== itm.name && (
                                                  <div className="text-[10px] text-muted-foreground">{itm.description}</div>
                                                )}
                                                {itm.hsnCode && (
                                                  <div className="text-[9.5px] font-mono text-muted-foreground">HSN: {itm.hsnCode}</div>
                                                )}
                                              </td>
                                              <td className="py-2 px-3">
                                                {matchedItem ? (
                                                  <div className="flex flex-col">
                                                    <span className="font-semibold text-foreground truncate max-w-[150px]" title={matchedItem.name}>
                                                      {matchedItem.name}
                                                    </span>
                                                    <span className="text-[9.5px] font-mono text-[#2563EB] dark:text-blue-400 font-semibold">
                                                      ID: {matchedItem.id}
                                                    </span>
                                                  </div>
                                                ) : targetItemId ? (
                                                  <span className="font-mono text-[10px] text-[#2563EB] dark:text-blue-400 font-semibold">ID: {targetItemId}</span>
                                                ) : (
                                                  <span className="text-muted-foreground italic text-[11px]">Unlinked</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-3">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                  isService ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20" : "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
                                                }`}>
                                                  {isService ? "Service" : "Goods"}
                                                </span>
                                              </td>
                                              <td className="py-2 px-3 text-right font-mono font-medium text-foreground">
                                                {qty} {itemUnit}
                                              </td>
                                              <td className="py-2 px-3 text-right font-mono text-foreground">
                                                ₹{rate.toLocaleString("en-IN")}
                                              </td>
                                              <td className="py-2 px-3 text-right font-mono text-muted-foreground">
                                                {itm.gstRate !== undefined ? `${itm.gstRate}%` : "—"}
                                              </td>
                                              <td className="py-2 px-3 text-right font-mono font-bold text-foreground">
                                                ₹{amount.toLocaleString("en-IN")}
                                              </td>
                                              <td className="py-2 px-3">
                                                {isService ? (
                                                  <span className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground font-medium bg-muted border border-border/50 px-2 py-0.5 rounded-full">
                                                    <Tag className="h-3 w-3" /> Non-stock Service
                                                  </span>
                                                ) : v.status === "Approved" ? (
                                                  v.type === "Sales" || v.type === "Delivery Note" ? (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-blue-700 dark:text-blue-300 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full">
                                                      <PackageMinus className="h-3 w-3 text-blue-600 dark:text-blue-400" /> Stock Deducted (-{qty} {itemUnit})
                                                    </span>
                                                  ) : v.type === "Purchase" || v.type === "Receipt Note" ? (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                                                      <PackageCheck className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> Stock Added (+{qty} {itemUnit})
                                                    </span>
                                                  ) : v.type === "Credit Note" ? (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                                                      <PackageCheck className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> Stock Restored (+{qty} {itemUnit})
                                                    </span>
                                                  ) : v.type === "Debit Note" ? (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-amber-700 dark:text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                                                      <PackageMinus className="h-3 w-3 text-amber-600 dark:text-amber-400" /> Stock Deducted (-{qty} {itemUnit})
                                                    </span>
                                                  ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-muted-foreground bg-muted border border-border/50 px-2 py-0.5 rounded-full">
                                                      <PackageCheck className="h-3 w-3" /> Processed ({qty} {itemUnit})
                                                    </span>
                                                  )
                                                ) : v.status === "Cancelled" ? (
                                                  <span className="inline-flex items-center gap-1 text-[10.5px] text-destructive font-medium bg-destructive/10 border border-destructive/20 px-2 py-0.5 rounded-full">
                                                    <PackageX className="h-3 w-3" /> Cancelled / Reversed
                                                  </span>
                                                ) : (
                                                  <span className="inline-flex items-center gap-1 text-[10.5px] text-amber-700 dark:text-amber-300 font-medium bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
                                                    <Clock className="h-3 w-3 text-amber-500" /> Pending Approval
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Double-Entry Ledger Breakdown */}
                            <div className="space-y-1.5 pt-1">
                              <div className="text-xs font-semibold text-muted-foreground">
                                Double-Entry Financial Postings for <span className="text-[#2563EB] dark:text-blue-400 font-semibold hover:text-blue-700 transition-colors">{v.voucherNumber}</span>
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-border">
                                    <th className="text-left py-1.5 font-medium text-muted-foreground">Ledger</th>
                                    <th className="text-right py-1.5 font-medium text-muted-foreground">Debit (₹)</th>
                                    <th className="text-right py-1.5 font-medium text-muted-foreground">Credit (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {v.lines.map((line) => (
                                    <tr key={line.id} className="border-b border-border/30">
                                      <td className="py-1.5 text-foreground">{line.ledgerName}</td>
                                      <td className="py-1.5 text-right font-medium">{line.debit > 0 ? `₹${line.debit.toLocaleString("en-IN")}` : "—"}</td>
                                      <td className="py-1.5 text-right font-medium">{line.credit > 0 ? `₹${line.credit.toLocaleString("en-IN")}` : "—"}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-semibold">
                                    <td className="py-1.5 text-foreground">Total</td>
                                    <td className="py-1.5 text-right">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                                    <td className="py-1.5 text-right">₹{v.totalCredit.toLocaleString("en-IN")}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {v.gstAmount && v.gstAmount > 0 && (
                              <div className="text-xs text-muted-foreground">
                                GST: ₹{v.gstAmount.toLocaleString("en-IN")}
                                {v.cgst ? ` (CGST: ₹${v.cgst.toLocaleString("en-IN")})` : ""}
                                {v.sgst ? ` (SGST: ₹${v.sgst.toLocaleString("en-IN")})` : ""}
                                {v.igst ? ` (IGST: ₹${v.igst.toLocaleString("en-IN")})` : ""}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-muted-foreground">No {title.toLowerCase()} found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
